import React, { useState, useEffect } from 'react';
import './EditorStyles.css';

// Value Modal Component
const ValueModal = ({ value, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    value_ar: '',
    value_en: '',
    display_order: 0
  });

  useEffect(() => {
    if (value) {
      setFormData({
        value_ar: value.value_ar || '',
        value_en: value.value_en || '',
        display_order: value.display_order || 0
      });
    }
  }, [value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="editor-modal-overlay" onClick={onClose}>
      <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="editor-modal-header">
          <h2>{value ? 'تعديل القيمة' : 'إضافة قيمة جديدة'}</h2>
          <button className="editor-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="editor-grid">
            <div className="editor-column">
              <h3>المحتوى العربي</h3>
              <div className="editor-form-group">
                <label>القيمة *</label>
                <textarea
                  rows="3"
                  value={formData.value_ar}
                  onChange={(e) => setFormData({...formData, value_ar: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="editor-column">
              <h3>المحتوى الإنجليزي</h3>
              <div className="editor-form-group">
                <label>Value (English) *</label>
                <textarea
                  rows="3"
                  value={formData.value_en}
                  onChange={(e) => setFormData({...formData, value_en: e.target.value})}
                  required
                />
              </div>
            </div>
          </div>

          <div className="editor-form-group">
            <label>ترتيب العرض</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
            />
          </div>

          <div className="editor-actions">
            <button type="submit" className="editor-btn-save">
              {value ? 'حفظ التغييرات' : 'إضافة القيمة'}
            </button>
            <button type="button" className="editor-btn-secondary" onClick={onClose}>
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AboutEditor = () => {
  const [content, setContent] = useState({});
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showValueModal, setShowValueModal] = useState(false);
  const [editingValue, setEditingValue] = useState(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/content/all`);
      const data = await response.json();
      if (data.success) {
        setContent(data.data.about || {});
        setValues(data.data.aboutValues || []);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        const baseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net';
        const imageUrl = `${baseUrl}${data.url.startsWith('/') ? data.url : `/${data.url}`}`;
        setContent(prev => ({ ...prev, [field]: imageUrl }));
        setMessage('تم رفع الصورة بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('فشل رفع الصورة');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('خطأ في رفع الصورة');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/content/about`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      const data = await response.json();
      if (data.success) {
        setMessage('تم حفظ قسم من نحن بنجاح!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'خطأ في حفظ المحتوى');
      }
    } catch (error) {
      setMessage('خطأ في الاتصال');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="editor-loading">جاري التحميل...</div>;
  }

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>محرر قسم من نحن</h2>
        {message && (
          <div className={`editor-message ${message.includes('نجح') || message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>

      <div className="editor-grid">
        <div className="editor-column">
          <h3>المحتوى العربي</h3>
          <div className="editor-form-group">
            <label>العنوان</label>
            <input
              type="text"
              value={content.title_ar || ''}
              onChange={(e) => setContent({...content, title_ar: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>اسم الشركة</label>
            <input
              type="text"
              value={content.company_name_ar || ''}
              onChange={(e) => setContent({...content, company_name_ar: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>الوصف الأول</label>
            <textarea
              rows="5"
              value={content.description1_ar || ''}
              onChange={(e) => setContent({...content, description1_ar: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>الوصف الثاني</label>
            <textarea
              rows="5"
              value={content.description2_ar || ''}
              onChange={(e) => setContent({...content, description2_ar: e.target.value})}
            />
          </div>
        </div>

        <div className="editor-column">
          <h3>المحتوى الإنجليزي</h3>
          <div className="editor-form-group">
            <label>Title (English)</label>
            <input
              type="text"
              value={content.title_en || ''}
              onChange={(e) => setContent({...content, title_en: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>Company Name (English)</label>
            <input
              type="text"
              value={content.company_name_en || ''}
              onChange={(e) => setContent({...content, company_name_en: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>Description 1 (English)</label>
            <textarea
              rows="5"
              value={content.description1_en || ''}
              onChange={(e) => setContent({...content, description1_en: e.target.value})}
            />
          </div>
          <div className="editor-form-group">
            <label>Description 2 (English)</label>
            <textarea
              rows="5"
              value={content.description2_en || ''}
              onChange={(e) => setContent({...content, description2_en: e.target.value})}
            />
          </div>
        </div>
      </div>

      <div className="editor-images-section">
        <h3 style={{ marginTop: '30px', marginBottom: '20px', color: '#815327', paddingBottom: '10px', borderBottom: '2px solid #e0e0e0' }}>
          إدارة الصور
        </h3>

        <div className="editor-form-group">
          <label>الصورة الأولى</label>
          <div className="editor-image-input">
            <input
              type="text"
              value={content.image1_url || ''}
              onChange={(e) => setContent({...content, image1_url: e.target.value})}
              placeholder="رابط الصورة أو رفع ملف"
            />
            <label className="editor-upload-btn">
              رفع صورة
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('image1_url', e.target.files[0])}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {content.image1_url && (
            <div style={{ marginTop: '15px' }}>
              <img 
                src={content.image1_url && (content.image1_url.startsWith('http') ? content.image1_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${content.image1_url.startsWith('/') ? content.image1_url : `/${content.image1_url}`}`)} 
                alt="Preview 1" 
                className="editor-image-preview"
              />
            </div>
          )}
        </div>

        <div className="editor-form-group">
          <label>الصورة الثانية</label>
          <div className="editor-image-input">
            <input
              type="text"
              value={content.image2_url || ''}
              onChange={(e) => setContent({...content, image2_url: e.target.value})}
              placeholder="رابط الصورة أو رفع ملف"
            />
            <label className="editor-upload-btn">
              رفع صورة
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('image2_url', e.target.files[0])}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {content.image2_url && (
            <div style={{ marginTop: '15px' }}>
              <img 
                src={content.image2_url && (content.image2_url.startsWith('http') ? content.image2_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${content.image2_url.startsWith('/') ? content.image2_url : `/${content.image2_url}`}`)} 
                alt="Preview 2" 
                className="editor-image-preview"
              />
            </div>
          )}
        </div>

        <div className="editor-form-group">
          <label>الصورة الثالثة</label>
          <div className="editor-image-input">
            <input
              type="text"
              value={content.image3_url || ''}
              onChange={(e) => setContent({...content, image3_url: e.target.value})}
              placeholder="رابط الصورة أو رفع ملف"
            />
            <label className="editor-upload-btn">
              رفع صورة
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('image3_url', e.target.files[0])}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {content.image3_url && (
            <div style={{ marginTop: '15px' }}>
              <img 
                src={content.image3_url && (content.image3_url.startsWith('http') ? content.image3_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${content.image3_url.startsWith('/') ? content.image3_url : `/${content.image3_url}`}`)} 
                alt="Preview 3" 
                className="editor-image-preview"
              />
            </div>
          )}
        </div>

        <div className="editor-form-group">
          <label>شعار الشركة</label>
          <div className="editor-image-input">
            <input
              type="text"
              value={content.logo_url || ''}
              onChange={(e) => setContent({...content, logo_url: e.target.value})}
              placeholder="رابط الصورة أو رفع ملف"
            />
            <label className="editor-upload-btn">
              رفع صورة
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('logo_url', e.target.files[0])}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {content.logo_url && (
            <div style={{ marginTop: '15px' }}>
              <img 
                src={content.logo_url && (content.logo_url.startsWith('http') ? content.logo_url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://server.acpca.net'}${content.logo_url.startsWith('/') ? content.logo_url : `/${content.logo_url}`}`)} 
                alt="Logo Preview" 
                className="editor-image-preview"
              />
            </div>
          )}
        </div>
      </div>

      <div className="editor-images-section">
        <h3 style={{ marginTop: '30px', marginBottom: '20px', color: '#815327', paddingBottom: '10px', borderBottom: '2px solid #e0e0e0' }}>
          قيم الشركة
        </h3>
        <button 
          className="editor-btn-add" 
          onClick={() => {
            setEditingValue(null);
            setShowValueModal(true);
          }}
          style={{ marginBottom: '20px' }}
        >
          + إضافة قيمة جديدة
        </button>

        <div className="editor-items-list">
          {values.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>لا توجد قيم</p>
          ) : (
            values.map((value) => (
              <div key={value.id} className="editor-item-card">
                <div className="editor-item-content">
                  <h4>القيمة {value.id}</h4>
                  <p><strong>عربي:</strong> {value.value_ar}</p>
                  <p><strong>إنجليزي:</strong> {value.value_en}</p>
                  <p><small>الترتيب: {value.display_order}</small></p>
                </div>
                <div className="editor-item-actions">
                  <button 
                    className="editor-btn-sm editor-btn-edit" 
                    onClick={() => {
                      setEditingValue(value);
                      setShowValueModal(true);
                    }}
                  >
                    تعديل
                  </button>
                  <button 
                    className="editor-btn-sm editor-btn-delete" 
                    onClick={async () => {
                      if (!window.confirm('هل أنت متأكد من حذف هذه القيمة؟')) return;
                      try {
                        const token = localStorage.getItem('dashboard_token');
                        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/content/about/values/${value.id}`, {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        const data = await response.json();
                        if (data.success) {
                          loadContent();
                          setMessage('تم حذف القيمة بنجاح!');
                          setTimeout(() => setMessage(''), 3000);
                        }
                      } catch (error) {
                        console.error('Delete error:', error);
                      }
                    }}
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showValueModal && (
        <ValueModal
          value={editingValue}
          onClose={() => {
            setShowValueModal(false);
            setEditingValue(null);
          }}
          onSave={async (formData) => {
            try {
              const token = localStorage.getItem('dashboard_token');
              const url = editingValue
                ? `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/content/about/values/${editingValue.id}`
                : `${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/content/about/values`;
              
              const method = editingValue ? 'PUT' : 'POST';

              const response = await fetch(url, {
                method,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
              });

              const data = await response.json();
              if (data.success) {
                setShowValueModal(false);
                setEditingValue(null);
                loadContent();
                setMessage(editingValue ? 'تم تحديث القيمة بنجاح!' : 'تم إضافة القيمة بنجاح!');
                setTimeout(() => setMessage(''), 3000);
              }
            } catch (error) {
              console.error('Save error:', error);
            }
          }}
        />
      )}

      <div className="editor-actions">
        <button className="editor-btn-save" onClick={handleSave} disabled={saving}>
          {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </button>
      </div>
    </div>
  );
};

export default AboutEditor;

